import { IEvent, IOtherSiteEvent, ITravelEvent } from "../../interfaces/event";
import { IResource, ISiteLocation } from "../../interfaces/resource";

export type SchedulerDisplayEvent = IEvent | IOtherSiteEvent | ITravelEvent;
export type SchedulerResourceRowType = 'site-group' | 'resource';

export interface ISchedulerResourceRowMeta {
    rowType: SchedulerResourceRowType;
    siteLocationId?: string;
    siteLocationName?: string;
    actualResourceId?: string;
    isUnassigned?: boolean;
}

type FullCalendarResourceInput = {
    id: string;
    title: string;
    index?: number;
    children?: FullCalendarResourceInput[];
    expanded?: boolean;
    extendedProps?: Record<string, unknown>;
};

const SITE_GROUP_PREFIX = 'site';
const RESOURCE_SEGMENT = 'resource';

const buildSiteGroupRowId = (siteLocationId: string) => `${SITE_GROUP_PREFIX}:${siteLocationId}`;
const buildSiteResourceRowId = (siteLocationId: string, resourceId: string) => `${SITE_GROUP_PREFIX}:${siteLocationId}:${RESOURCE_SEGMENT}:${resourceId}`;

const getResourceSiteIds = (resource: IResource, allowedSiteIds: string[]) => {
    return (resource.siteLocation ?? [])
        .map((site) => site.illumina_sitelocationid)
        .filter((siteId) => allowedSiteIds.includes(siteId));
};

const cloneSchedulerEvent = <TEvent extends SchedulerDisplayEvent>(event: TEvent, nextId: string, nextResourceId: string): TEvent => {
    return {
        ...event,
        id: nextId,
        resourceId: nextResourceId,
    };
};

export const parseSchedulerResourceId = (resourceId?: string | null): ISchedulerResourceRowMeta => {
    const normalized = String(resourceId ?? '').trim();
    if (!normalized) {
        return { rowType: 'resource' };
    }

    const match = normalized.match(/^site:([^:]+):resource:(.+)$/i);
    if (match) {
        return {
            rowType: 'resource',
            siteLocationId: match[1],
            actualResourceId: match[2],
            isUnassigned: match[2] === '0',
        };
    }

    const siteMatch = normalized.match(/^site:([^:]+)$/i);
    if (siteMatch) {
        return {
            rowType: 'site-group',
            siteLocationId: siteMatch[1],
        };
    }

    return {
        rowType: 'resource',
        actualResourceId: normalized,
        isUnassigned: normalized === '0',
    };
};

export const getSchedulerRowMetaFromResource = (resource: any): ISchedulerResourceRowMeta => {
    const meta = resource?.extendedProps?.schedulerRowMeta;
    if (meta) {
        return meta as ISchedulerResourceRowMeta;
    }

    return parseSchedulerResourceId(resource?.id);
};

export const buildSchedulerResources = (
    resources: IResource[],
    sites: ISiteLocation[],
    isRegion: boolean,
): FullCalendarResourceInput[] => {
    if (!isRegion) {
        return resources as unknown as FullCalendarResourceInput[];
    }

    const sortedResources = [...resources]
        .filter((resource) => resource.id !== '0')
        .sort((left, right) => {
            if (left.index !== right.index) {
                return left.index - right.index;
            }
            return String(left.title ?? '').localeCompare(String(right.title ?? ''));
        });

    return sites.map((site, siteIndex) => {
        const siteChildren: FullCalendarResourceInput[] = [
            {
                id: buildSiteResourceRowId(site.illumina_sitelocationid, '0'),
                title: 'Unassigned',
                index: 0,
                extendedProps: {
                    id: buildSiteResourceRowId(site.illumina_sitelocationid, '0'),
                    title: 'Unassigned',
                    index: 0,
                    characteristics: [],
                    categories: [],
                    approvedLeaves: [],
                    schedulerRowMeta: {
                        rowType: 'resource',
                        siteLocationId: site.illumina_sitelocationid,
                        siteLocationName: site.illumina_name,
                        actualResourceId: '0',
                        isUnassigned: true,
                    } as ISchedulerResourceRowMeta,
                },
            },
        ];

        sortedResources.forEach((resource) => {
            if (!getResourceSiteIds(resource, [site.illumina_sitelocationid]).includes(site.illumina_sitelocationid)) {
                return;
            }

            siteChildren.push({
                id: buildSiteResourceRowId(site.illumina_sitelocationid, resource.id),
                title: resource.title,
                index: resource.index,
                extendedProps: {
                    ...resource,
                    schedulerRowMeta: {
                        rowType: 'resource',
                        siteLocationId: site.illumina_sitelocationid,
                        siteLocationName: site.illumina_name,
                        actualResourceId: resource.id,
                        isUnassigned: resource.id === '0',
                    } as ISchedulerResourceRowMeta,
                },
            });
        });

        return {
            id: buildSiteGroupRowId(site.illumina_sitelocationid),
            title: site.illumina_name,
            index: siteIndex,
            expanded: true,
            children: siteChildren,
            extendedProps: {
                schedulerRowMeta: {
                    rowType: 'site-group',
                    siteLocationId: site.illumina_sitelocationid,
                    siteLocationName: site.illumina_name,
                } as ISchedulerResourceRowMeta,
            },
        };
    });
};

export const mapEventsToSchedulerRows = (
    events: SchedulerDisplayEvent[],
    resources: IResource[],
    siteIds: string[],
    isRegion: boolean,
): SchedulerDisplayEvent[] => {
    if (!isRegion) {
        return events;
    }

    const resourcesById = new Map<string, IResource>();
    resources.forEach((resource) => resourcesById.set(resource.id, resource));

    const mappedEvents: SchedulerDisplayEvent[] = [];
    events.forEach((event) => {
        if ((event as IOtherSiteEvent)?.otherSite) {
            return;
        }

        const eventSiteId = String((event as IEvent)?.siteLocationDetails?.id ?? '').trim();
        if (eventSiteId) {
            mappedEvents.push(cloneSchedulerEvent(
                event,
                event.id,
                buildSiteResourceRowId(eventSiteId, String(event.resourceId ?? '0'))
            ));
            return;
        }

        const actualResourceId = String(event.resourceId ?? '').trim();
        if (!actualResourceId || actualResourceId === '0') {
            mappedEvents.push(event);
            return;
        }

        const resource = resourcesById.get(actualResourceId);
        const resourceSiteIds = resource ? getResourceSiteIds(resource, siteIds) : [];
        if (resourceSiteIds.length < 1) {
            mappedEvents.push(event);
            return;
        }

        resourceSiteIds.forEach((siteId, index) => {
            mappedEvents.push(cloneSchedulerEvent(
                event,
                resourceSiteIds.length > 1 ? `${event.id}__site__${siteId}` : event.id,
                buildSiteResourceRowId(siteId, actualResourceId)
            ));
        });
    });

    return mappedEvents;
};
